import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Card, Button, OtpInput } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';

export function MfaChallengePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { mfaPending, pendingUser, verifyMfa, cancelMfa, loading, error, clearError } =
    useAuthStore();
  const toast = useToast();

  // Preserve redirect parameter (e.g., from /invite/{token})
  const redirectUrl = searchParams.get('redirect') || '/';

  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [localError, setLocalError] = useState('');

  // Track successful verification to prevent redirect to login during navigation
  const isVerifiedRef = useRef(false);

  // Redirect if no MFA pending (but not if we just verified successfully)
  useEffect(() => {
    if (!isVerifiedRef.current && (!mfaPending || !pendingUser)) {
      navigate('/login');
    }
  }, [mfaPending, pendingUser, navigate]);

  const handleVerify = async (verificationCode: string) => {
    setLocalError('');
    clearError();

    try {
      const result = await verifyMfa(verificationCode);

      if (result.success) {
        // Mark as verified BEFORE navigation to prevent useEffect from redirecting to login
        isVerifiedRef.current = true;

        if (result.usedBackupCode) {
          toast.success('Logged in with backup code');
        } else {
          toast.success('Welcome back!');
        }
        navigate(redirectUrl);
      } else {
        setLocalError('Invalid code. Please try again.');
      }
    } catch {
      setLocalError('Verification failed. Please try again.');
    }
  };

  const handleCancel = async () => {
    await cancelMfa();
    navigate('/login');
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setCode('');
    setLocalError('');
    clearError();
  };

  if (!mfaPending || !pendingUser) {
    return null;
  }

  return (
    <Card padding="lg" className="max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full mb-4">
          <Shield className="h-6 w-6 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Two-Factor Authentication</h2>
        <p className="text-gray-600 mt-1">
          {useBackupCode
            ? 'Enter one of your backup codes'
            : 'Enter the 6-digit code from your authenticator app'}
        </p>
      </div>

      <div className="space-y-6">
        {useBackupCode ? (
          // Backup code input
          <div className="space-y-4">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              maxLength={9}
              className="w-full text-center text-xl font-mono tracking-widest px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
              disabled={loading}
            />
            {(localError || error) && (
              <p className="text-sm text-red-600 text-center">{localError || error}</p>
            )}
          </div>
        ) : (
          // OTP input
          <OtpInput
            value={code}
            onChange={setCode}
            onComplete={handleVerify}
            error={localError || error || undefined}
            disabled={loading}
          />
        )}

        <div className="space-y-3">
          <Button
            onClick={() => handleVerify(code)}
            className="w-full"
            loading={loading}
            disabled={useBackupCode ? code.length < 8 : code.length !== 6}
          >
            Verify
          </Button>

          <button
            type="button"
            onClick={toggleBackupCode}
            className="w-full text-sm text-primary-600 hover:text-primary-700"
            disabled={loading}
          >
            {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code'}
          </button>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="w-full"
            leftIcon={<ArrowLeft className="h-4 w-4" />}
            disabled={loading}
          >
            Back to Login
          </Button>
        </div>
      </div>
    </Card>
  );
}
